<script lang="ts">
  import { onMount } from 'svelte';
  import { ReportCadenceLabel } from '../lib/report-cadence-label';
  import { ReportRecipientsEditor } from '../lib/report-recipients-editor.svelte';
  import { navigation } from '../lib/navigation.svelte';

  interface Props {
    onUnauthorized: () => void;
  }

  let { onUnauthorized }: Props = $props();

  const editor = new ReportRecipientsEditor(() => onUnauthorized());
  editor.watch();

  onMount(() => editor.loadClients());
</script>

<section class="recipients">
  <header class="intro">
    <h1>Lista de envios</h1>
    <p>Quem recebe o relatório dos vendedores de cada cliente: no WhatsApp, gente do HQ; no e-mail, quem tem a função no cliente.</p>
  </header>

  {#if editor.error}
    <p class="banner banner-error" role="alert">{editor.error}</p>
  {/if}

  <div class="columns">
    <nav class="clients" aria-label="Clientes">
      {#each editor.clients as option (option.tenantId)}
        <button
          class="client"
          class:active={option.tenantId === editor.tenantId}
          onclick={() => navigation.merge({ cliente: option.tenantId })}
        >
          {option.name}
        </button>
      {:else}
        <p class="empty">Nenhum cliente ativo com alguém que receba o relatório dos vendedores.</p>
      {/each}
    </nav>

    {#if editor.client}
      <div class="detail">
        <article class="card">
          <h2>Relatório dos vendedores no WhatsApp <span class="count">{editor.recipients?.whatsapp.length ?? 0}</span></h2>
          {#if !editor.recipients}
            <p class="empty">carregando…</p>
          {:else}
            <ul class="rows">
              {#each editor.recipients.whatsapp as row (row.contact.identification ?? row.contact.id)}
                <li class="row">
                  <span class="who">
                    <span class="name">{row.contact.name}</span>
                    {#if row.contact.identification}
                      <span class="sub mono">{row.contact.identification}</span>
                    {/if}
                  </span>
                  <span class="subscriptions">
                    {#each row.subscriptions as subscription (subscription.recipientId)}
                      <span class="badge">
                        {ReportCadenceLabel.of(subscription.cadence)}
                        <button
                          class="badge-remove"
                          disabled={editor.busy}
                          title="Tirar do {ReportCadenceLabel.of(subscription.cadence).toLowerCase()}"
                          aria-label="Tirar {row.contact.name} do {ReportCadenceLabel.of(subscription.cadence).toLowerCase()}"
                          onclick={() => editor.remove(subscription.recipientId)}
                        >
                          Tirar
                        </button>
                      </span>
                    {/each}
                  </span>
                </li>
              {:else}
                <li class="empty">Ninguém recebe no WhatsApp ainda.</li>
              {/each}
            </ul>
          {/if}

          <div class="add">
            <h3>Incluir contato do HQ</h3>
            <div class="add-controls">
              <input
                type="search"
                placeholder="Buscar por nome ou telefone"
                aria-label="Buscar contato do HQ"
                bind:value={editor.search.term}
              />
              <select aria-label="Cadência" bind:value={editor.cadence}>
                {#each ReportCadenceLabel.options() as option (option.cadence)}
                  <option value={option.cadence}>{option.label}</option>
                {/each}
              </select>
            </div>
            {#if editor.search.results.length > 0}
              <ul class="rows results">
                {#each editor.search.results as contact (contact.id)}
                  <li class="row">
                    <span class="who">
                      <span class="name">{contact.name}</span>
                      {#if contact.identification}
                        <span class="sub mono">{contact.identification}</span>
                      {/if}
                    </span>
                    {#if editor.isListed(contact)}
                      <span class="sub">já na lista</span>
                    {:else}
                      <button class="ghost" disabled={editor.busy} onclick={() => editor.add(contact)}>
                        Incluir
                      </button>
                    {/if}
                  </li>
                {/each}
              </ul>
            {:else if editor.search.isSearching}
              <p class="empty">Nenhum contato do HQ com esse nome.</p>
            {/if}
          </div>
        </article>

        <article class="card">
          <h2>Relatório dos vendedores por e-mail <span class="count">{editor.recipients?.email.length ?? 0}</span></h2>
          <p class="hint">Recebe quem tem, no cliente, a função de receber o relatório. Só leitura.</p>
          <ul class="rows">
            {#each editor.recipients?.email ?? [] as person (person.email)}
              <li class="row">
                <span class="who">
                  <span class="name">{person.name}</span>
                  <span class="sub mono">{person.email}</span>
                </span>
              </li>
            {:else}
              <li class="empty">Ninguém com a função no cliente.</li>
            {/each}
          </ul>
        </article>
      </div>
    {/if}
  </div>
</section>

<style>
  .recipients {
    display: flex;
    flex-direction: column;
    gap: var(--space-5);
  }
  .intro h1 {
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 var(--space-1);
  }
  .intro p,
  .hint {
    color: var(--text-secondary);
    font-size: 13px;
    margin: 0;
  }
  .banner {
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    font-size: 13px;
  }
  .banner-error {
    background: var(--color-danger-bg);
    border: 1px solid var(--color-danger-border);
    color: var(--color-danger);
  }
  .columns {
    display: grid;
    grid-template-columns: 220px 1fr;
    gap: var(--space-5);
    align-items: start;
  }
  .clients {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .client {
    text-align: left;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    color: var(--text-secondary);
    font-size: 13px;
    cursor: pointer;
    transition: all var(--duration-fast) var(--easing-default);
  }
  .client:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  .client.active {
    background: var(--accent-bg);
    color: var(--text-primary);
  }
  .detail {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .card {
    background: var(--surface-card);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-lg);
    padding: var(--space-4) var(--space-5);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  .card h2 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .card h3 {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 var(--space-2);
  }
  .count {
    font-size: 11px;
    color: var(--text-tertiary);
    background: var(--bg-hover);
    border-radius: var(--radius-sm);
    padding: 0 var(--space-2);
  }
  .rows {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) 0;
    border-bottom: 1px solid var(--border-subtle);
  }
  .row:last-child {
    border-bottom: none;
  }
  .who {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .name {
    font-size: 13px;
  }
  .sub {
    font-size: 12px;
    color: var(--text-tertiary);
  }
  .badge {
    font-size: 11px;
    padding: 2px var(--space-2);
    border-radius: var(--radius-sm);
    background: var(--color-info-bg);
    border: 1px solid var(--color-info-border);
    color: var(--color-info);
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
  }
  .subscriptions {
    display: flex;
    gap: var(--space-2);
  }
  .badge-remove {
    font-size: 11px;
    line-height: 1;
    padding: 1px var(--space-1);
    border-left: 1px solid var(--color-info-border);
    border-radius: var(--radius-sm);
    color: inherit;
    cursor: pointer;
    opacity: 0.7;
  }
  .badge-remove:hover:not(:disabled) {
    opacity: 1;
    color: var(--color-danger);
  }
  .badge-remove:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .ghost {
    font-size: 12px;
    padding: var(--space-1) var(--space-3);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-strong);
    color: var(--text-secondary);
    cursor: pointer;
  }
  .ghost:hover:not(:disabled) {
    background: var(--bg-hover);
    color: var(--text-primary);
  }
  .ghost.danger:hover:not(:disabled) {
    color: var(--color-danger);
    border-color: var(--color-danger-border);
  }
  .ghost:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .add {
    border-top: 1px solid var(--border-subtle);
    padding-top: var(--space-3);
  }
  .add-controls {
    display: flex;
    gap: var(--space-2);
  }
  .add-controls input {
    flex: 1;
  }
  .add-controls input,
  .add-controls select {
    background: var(--bg-base);
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
    padding: var(--space-2) var(--space-3);
    font-size: 13px;
  }
  .results {
    margin-top: var(--space-2);
  }
  .empty {
    color: var(--text-tertiary);
    font-size: 13px;
    padding: var(--space-2) 0;
  }
</style>
